import React, { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getSession } from './auth';
import { SCREENS, trackScreen } from './analytics';
import {
  MAX_BIO, MAX_NAME, addMember, deleteBand, getUserName, loadBand, loadDirectory, removeMember, saveBand,
} from './bands';

const normalize = (text) => (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// A band's profile (/b/:bandId). Everyone can read it; its members can edit it and add or remove members.
const Band = ({ bandId }) => {
  useEffect(() => { trackScreen(SCREENS.band); }, []);
  const me = getSession().username;
  const [band, setBand] = useState(undefined); // undefined = loading, null = not found
  const [names, setNames] = useState({});
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', bio: '' });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [directory, setDirectory] = useState(null);
  const [gone, setGone] = useState(false);

  const showNames = (usernames) => usernames.forEach((u) => getUserName(u).then((name) => {
    if (name) setNames((prev) => ({ ...prev, [u]: name }));
  }));

  useEffect(() => {
    let live = true;
    loadBand(bandId).then((b) => {
      if (!live) return;
      setBand(b);
      if (b) showNames(b.members);
    }).catch(() => { if (live) { setBand(null); toast.error("Couldn't load band ❌"); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bandId]);

  const isMember = !!band && band.members.includes(me);
  useEffect(() => {
    if (isMember && !directory) loadDirectory().then(setDirectory).catch(() => toast.error("Couldn't load people ❌"));
  }, [isMember, directory]);

  if (gone) return <Redirect to="/profile" />;
  if (band === undefined) return <div className="text-center"><Spinner animation="border" /></div>;
  if (!band) {
    return (
      <div className="text-center px-3 py-5">
        <p className="text-muted">We couldn't find that band.</p>
        <Link to="/profile">← Profile</Link>
      </div>
    );
  }

  const nameOf = (u) => names[u] || u;

  // Re-reads the band after a change so the page shows what was saved.
  const refresh = async () => {
    const fresh = await loadBand(bandId);
    setBand(fresh);
    if (fresh) showNames(fresh.members);
    return fresh;
  };

  const run = async (work, failure) => {
    setBusy(true);
    try {
      await work();
      return true;
    } catch (e) {
      toast.error(failure);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return toast.error("Band name can't be empty");
    if (await run(async () => { await saveBand(bandId, draft); await refresh(); }, "Couldn't save band ❌")) {
      setEditing(false);
      toast.success('Band saved ✅');
    }
  };

  const add = async (user) => {
    const ok = await run(async () => { await addMember(bandId, user.username); await refresh(); }, "Couldn't add member ❌");
    if (ok) {
      setQuery('');
      setNames((prev) => ({ ...prev, [user.username]: user.name || prev[user.username] }));
      toast.success(`${user.name || user.username} added to the band ✅`);
    }
  };

  const remove = async (username) => {
    const self = username === me;
    if (!window.confirm(self ? `Leave ${band.name}?` : `Remove ${nameOf(username)} from ${band.name}?`)) return;
    const ok = await run(async () => {
      await removeMember(bandId, username);
      if (self) setGone(true); else await refresh();
    }, "Couldn't remove member ❌");
    if (ok && !self) toast.success('Removed from the band');
  };

  const destroy = async () => {
    if (!window.confirm(`Delete ${band.name}? Albums and songs that credit it will stop showing it.`)) return;
    if (await run(() => deleteBand(band), "Couldn't delete band ❌")) setGone(true);
  };

  const q = normalize(query.trim());
  const found = q && directory
    ? directory.filter((u) => !band.members.includes(u.username)
        && (normalize(u.name).includes(q) || normalize(u.username).includes(q))).slice(0, 6)
    : [];

  return (
    <div style={{ maxWidth: 640, width: '100%', margin: '0 auto', paddingBottom: 40 }}>
      <div className="px-3 py-2"><Link to={isMember ? '/profile' : '/user'}>← {isMember ? 'Profile' : 'Albums'}</Link></div>

      <div className="px-3">
        {editing ? (
          <Form onSubmit={save}>
            <Form.Group controlId="band-name">
              <Form.Label>Band name</Form.Label>
              <Form.Control autoFocus maxLength={MAX_NAME} value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Form.Group>
            <Form.Group controlId="band-bio">
              <Form.Label>Bio</Form.Label>
              <Form.Control as="textarea" rows={3} maxLength={MAX_BIO} value={draft.bio}
                onChange={(e) => setDraft({ ...draft, bio: e.target.value })} />
            </Form.Group>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>{' '}
            <Button variant="secondary" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
          </Form>
        ) : (
          <React.Fragment>
            <h3 className="mb-0">{band.name}</h3>
            <div className="text-muted mb-3">Band</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{band.bio || <span className="text-muted">No bio</span>}</p>
            {isMember && (
              <Button variant="outline-primary" className="mb-3"
                onClick={() => { setDraft({ name: band.name, bio: band.bio }); setEditing(true); }}>Edit band</Button>
            )}
          </React.Fragment>
        )}

        <h5 className="mt-4">Members</h5>
        {band.members.map((u) => (
          <div key={u} className="d-flex align-items-center justify-content-between py-2 border-bottom">
            <div style={{ minWidth: 0 }}>
              <Link to={`/u/${encodeURIComponent(u)}`}>{nameOf(u)}</Link>
              <small className="text-muted ml-2">@{u}</small>
              {u === me && <Badge variant="secondary" className="ml-2">You</Badge>}
            </div>
            {isMember && (u === me ? band.members.length > 1 : true) && (
              <Button size="sm" variant="outline-danger" className="ml-2 flex-shrink-0" disabled={busy}
                onClick={() => remove(u)}>{u === me ? 'Leave' : 'Remove'}</Button>
            )}
          </div>
        ))}

        {isMember && (
          <div className="mt-4">
            <h6>Add a member</h6>
            <Form.Control placeholder={directory ? 'Search by name or username' : 'Loading people…'}
              disabled={!directory || busy} value={query} aria-label="Search people"
              onChange={(e) => setQuery(e.target.value)} />
            {found.map((user) => (
              <div key={user.username} className="d-flex align-items-center justify-content-between py-1">
                <div style={{ minWidth: 0 }}>{user.name || user.username}<small className="text-muted ml-2">@{user.username}</small></div>
                <Button size="sm" variant="outline-success" className="ml-2 flex-shrink-0" disabled={busy}
                  onClick={() => add(user)}>Add</Button>
              </div>
            ))}
            {q && directory && found.length === 0 && <div className="text-muted small mt-2">No one found.</div>}
            <small className="text-muted d-block mt-1">
              Every member owns the band equally: they can edit it, add people and remove people.
            </small>
          </div>
        )}

        {isMember && band.members.length === 1 && (
          <div className="mt-5 pt-3 border-top">
            <Button variant="outline-danger" disabled={busy} onClick={destroy}>Delete band</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Band;
